/**
 * Google Tag Manager Event Tracking Constants
 *
 * This file contains all event tracking constants and TypeScript types
 * for the Trade-In Offer Form GTM integration.
 *
 * @see google-event-tracking.md for full specification
 */
// ============================================================================
// CONSTANTS
// ============================================================================
/**
 * Main GTM event name for all form step completions
 */
export var GTM_EVENT_NAME = 'trade_in_form_step_completed';
/**
 * Form identifier constants
 */
export var FORM_CONSTANTS = {
    FORM_ID: 'trade_in_offer_form',
    FORM_NAME: 'Trade In Offer Form',
};
/**
 * Module/Component names that trigger events
 */
export var MODULE_NAMES = {
    MINI_REGO_FORM: 'MiniRegoForm',
    REGO_LOOKUP: 'RegoLookup',
    MANUAL_LOOKUP: 'ManualLookup',
    VEHICLES_LIST: 'VehiclesList',
    VEHICLE_OPTIONS_AND_PACKS: 'VehicleOptionsAndPacks',
    CUSTOMER_DETAILS: 'CustomerDetails',
    VEHICLE_CONDITION: 'VehicleCondition',
    BOOKING_PREFERENCES: 'BookingPreferences',
};
/**
 * Form step tracking events with metadata
 */
export var TRACKING_EVENTS = {
    /**
     * Step 0.5: Get Your Offer (MiniRegoForm)
     * Entry point from mini form widget
     */
    GET_YOUR_OFFER: {
        step: '0.5',
        stepName: 'get_your_offer',
        module: MODULE_NAMES.MINI_REGO_FORM,
        description: 'User clicked GET YOUR OFFER button (entry point)',
    },
    /**
     * Step 0.5: Find Your Car (RegoLookup)
     * Alternative entry point via rego lookup
     */
    FIND_YOUR_CAR: {
        step: '0.5',
        stepName: 'find_your_car',
        module: MODULE_NAMES.REGO_LOOKUP,
        description: 'User clicked Find Your Car button (rego lookup)',
    },
    /**
     * Step 0.5: Manual Vehicle Selection (ManualLookup)
     * Alternative entry point via manual selection
     */
    MANUAL_VEHICLE_SELECTION: {
        step: '0.5',
        stepName: 'manual_vehicle_selection',
        module: MODULE_NAMES.MANUAL_LOOKUP,
        description: 'User clicked NEXT button (manual lookup)',
    },
    /**
     * Step 1: Vehicle Confirmed (VehiclesList)
     * User confirms vehicle selection
     */
    VEHICLE_CONFIRMED: {
        step: '1',
        stepName: 'vehicle_confirmed',
        module: MODULE_NAMES.VEHICLES_LIST,
        description: 'User clicked YES, THATS MY CAR button',
    },
    /**
     * Step 1.5: Vehicle Options and Packs (VehicleOptionsAndPacks)
     * User completes optional extras selection
     */
    VEHICLE_OPTIONS_AND_PACKS: {
        step: '1.5',
        stepName: 'vehicle_options_and_packs',
        module: MODULE_NAMES.VEHICLE_OPTIONS_AND_PACKS,
        description: 'User clicked NEXT button (optional extras)',
    },
    /**
     * Step 2: Customer Details (CustomerDetails)
     * User submits personal information
     */
    CUSTOMER_DETAILS: {
        step: '2',
        stepName: 'customer_details',
        module: MODULE_NAMES.CUSTOMER_DETAILS,
        description: 'User clicked CONTINUE button (customer details)',
    },
    /**
     * Step 3: Vehicle Condition & Valuation (VehicleCondition)
     * User submits vehicle condition - triggers estimate
     */
    VEHICLE_CONDITION_VALUATION: {
        step: '3',
        stepName: 'vehicle_condition_valuation',
        module: MODULE_NAMES.VEHICLE_CONDITION,
        description: 'User clicked NEXT button (vehicle condition)',
    },
    /**
     * Step 4: Book Inspection (BookingPreferences)
     * User chooses to proceed with booking
     */
    BOOK_INSPECTION: {
        step: '4',
        stepName: 'book_inspection',
        module: MODULE_NAMES.BOOKING_PREFERENCES,
        description: 'User clicked BOOK YOUR INSPECTION button',
    },
    /**
     * Step 4 (Skip): Not Ready for Inspection (BookingPreferences)
     * User opts to skip inspection booking
     */
    NO_INSPECTION_NOT_READY: {
        step: '4_skip',
        stepName: 'no_inspection_not_ready',
        module: MODULE_NAMES.BOOKING_PREFERENCES,
        description: 'User clicked I\'M NOT READY RIGHT NOW button',
    },
    /**
     * Step 5: Request Appointment (BookingPreferences)
     * PRIMARY CONVERSION - User submits appointment request
     */
    REQUEST_APPOINTMENT: {
        step: '5',
        stepName: 'request_appointment',
        module: MODULE_NAMES.BOOKING_PREFERENCES,
        description: 'User clicked REQUEST APPOINTMENT button (PRIMARY CONVERSION)',
    },
    /**
     * Step 5 Confirmed: Appointment Confirmed (BookingPreferences)
     * Success state - Thank you message displayed
     */
    APPOINTMENT_CONFIRMED: {
        step: '5_confirmed',
        stepName: 'appointment_confirmed',
        module: MODULE_NAMES.BOOKING_PREFERENCES,
        description: 'Thank you message displayed (booking confirmed)',
    },
};
